import React, { useState } from 'react';
import { Tv, Plane, GraduationCap, ShoppingBag, Waves } from 'lucide-react';
import { HomeValuationForm } from './HomeValuationForm';

const HIGHLIGHT_ICONS = [Plane, GraduationCap, ShoppingBag, Waves];

const FeatureVideo = ({ video }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <figure className="space-y-3" data-testid="area-feature-video">
      <p className="text-xs font-bold tracking-[0.25em] uppercase text-gold-600 flex items-center gap-2"><Tv className="w-4 h-4" />As Seen on American Dream TV</p>
      <div className="relative w-full overflow-hidden rounded-2xl border-2 border-gold-400/60 shadow-2xl bg-black" style={{ paddingBottom: '56.25%' }}>
        <video controls playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" aria-label={video.title} onError={() => setFailed(true)}>
          <source src={video.src} type="video/mp4" />
        </video>
      </div>
      <figcaption className="text-sm text-neutral-500">{video.title}</figcaption>
    </figure>
  );
};

const Highlights = ({ items, areaName }) => (
  <div data-testid="area-highlights">
    <h2 className="font-serif text-3xl sm:text-4xl text-[#143160] mb-6">Why Buyers Want {areaName}</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {items.map((h, i) => {
        const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
        return (
          <div key={h.title} className="bg-white rounded-2xl p-6 border border-gold-400/30 shadow-[0_2px_14px_rgba(20,49,96,0.06)]">
            <div className="w-11 h-11 rounded-xl bg-[#143160] text-gold-300 flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
            <h3 className="font-bold text-[#143160] leading-snug mb-2">{h.title}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{h.text}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const MarketSnapshot = ({ market }) => (
  <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
    <div>
      <h2 className="font-serif text-3xl sm:text-4xl text-[#143160] mb-4">{market.heading}</h2>
      <p className="text-neutral-600 leading-relaxed">Here's what's actually happening in our zip:</p>
      <p className="text-neutral-700 leading-relaxed mt-4 italic border-l-4 border-gold-500 pl-4">{market.note}</p>
    </div>
    <div className="grid sm:grid-cols-3 gap-4" data-testid="area-market-stats">
      {market.stats.map((s) => (
        <div key={s.label} className="bg-[#143160] text-white rounded-2xl p-6 text-center">
          <p className="font-serif text-3xl sm:text-4xl text-gold-300 font-black">{s.value}</p>
          <p className="text-xs text-white/75 mt-2 leading-snug">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
);

export const AreaValuationCTA = ({ cta, openPrivacyPolicy }) => (
  <section id="area-valuation-cta" className="bg-[#143160] py-16 sm:py-20 px-5" data-testid="area-valuation-cta">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <div className="text-white space-y-5 text-center lg:text-left">
        <p className="text-xs font-bold tracking-[0.3em] uppercase text-gold-300">Free Home Valuation</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="area-cta-headline">{cta.headline}</h2>
        <p className="text-white/80 leading-relaxed text-lg">{cta.text}</p>
      </div>
      <div className="w-full max-w-lg mx-auto lg:ml-auto">
        <HomeValuationForm openPrivacyPolicy={openPrivacyPolicy} variant="cta" />
      </div>
    </div>
  </section>
);

// Rich, hand-written content for priority seller pages (e.g. /sell/23454).
export const CustomAreaContent = ({ area }) => {
  const c = area.custom;
  return (
    <section className="max-w-6xl mx-auto px-5 py-16 space-y-16" data-testid="area-custom-content">
      {c.video && <FeatureVideo video={c.video} />}
      {c.highlights && <Highlights items={c.highlights} areaName={area.name} />}
      <MarketSnapshot market={c.market} />

      <div>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#143160] mb-6">Neighborhoods I Serve in {area.name}</h2>
        <div className="grid md:grid-cols-3 gap-6" data-testid="area-neighborhoods">
          {c.neighborhoods.map((n) => (
            <div key={n.name} className="bg-sand-50 rounded-2xl p-6 border border-[#143160]/10">
              <h3 className="font-serif text-2xl text-[#143160] mb-2">{n.name}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">{n.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#143160] text-white rounded-3xl p-8 sm:p-12">
        <h2 className="font-serif text-3xl sm:text-4xl mb-4">Why List With Jess</h2>
        <p className="text-white/85 leading-relaxed text-lg">{c.why}</p>
      </div>
    </section>
  );
};
